import React, { createContext, useContext, useState, useEffect } from "react";

type Language = "sv" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem("language");
    return (saved === "en" || saved === "sv" ? saved : "sv") as Language;
  });

  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

const translations: Record<Language, Record<string, string>> = {
  sv: {
    // Index/Landing page
    "hero.badge": "100% Gratis & Anonymt",
    "hero.title.learn": "Lär dig om",
    "hero.title.finance": "privatekonomi",
    "hero.description": "Ställ anonyma frågor om sparande, investeringar och ekonomi. Få svar från andra användare i communityn – helt kostnadsfritt.",
    "hero.button.chat": "Börja chatta gratis",
    "hero.button.faq": "Vanliga frågor",
    "hero.button.more": "Läs mer",
    "features.title": "Varför använda oss?",
    "features.subtitle": "Vi hjälper unga att ta kontroll över sin ekonomi genom objektiv information och community-stöd",
    "features.anonymous.title": "100% Anonymt",
    "features.anonymous.desc": "Ingen registrering krävs. Ställ frågor utan att oroa dig för din integritet.",
    "features.objective.title": "Objektiv Information",
    "features.objective.desc": "Alla svar baseras på trovärdiga källor. Inga specifika produktrekommendationer.",
    "features.young.title": "För Unga",
    "features.young.desc": "Förklaringar på enkel svenska med exempel som är relevanta för dig.",
    "howto.title": "Så här fungerar det",
    "howto.subtitle": "Tre enkla steg till bättre ekonomisk kunskap",
    "howto.step1.title": "Ställ din fråga",
    "howto.step1.desc": "Klicka på \"Börja chatta\" och skriv din fråga om ekonomi",
    "howto.step2.title": "Få svar direkt",
    "howto.step2.desc": "Vår AI-assistent ger dig objektiv information baserat på trovärdiga källor",
    "howto.step3.title": "Lär dig mer",
    "howto.step3.desc": "Ställ följdfrågor och fördjupa din kunskap om privatekonomi",
    "cta.title": "Redo att ta kontroll över din ekonomi?",
    "cta.subtitle": "Börja din resa mot bättre ekonomisk kunskap idag – helt gratis och anonymt",
    "cta.button": "Starta din första chatt",
    "footer.disclaimer": "Denna webbplats ger generell utbildning om privatekonomi. Vi ger inte personlig finansiell rådgivning.",
    
    // Navigation
    "nav.profile": "Profil",
    "nav.login": "Logga in",
    "nav.logout": "Logga ut",
    "nav.home": "Hem",
    
    // About page
    "about.title": "Om oss",
    "about.hero.title": "Vi hjälper unga att ta kontroll över sin",
    "about.hero.title.economy": "ekonomi",
    "about.hero.subtitle": "Vårat mål är att göra ekonomisk utbildning tillgänglig, begriplig och helt kostnadsfri för alla unga människor",
    "about.whatwedo.title": "Vad vi gör",
    "about.whatwedo.p1": "Vi driver en plattform där unga kan ställa anonyma frågor om privatekonomi, sparande och investeringar. All information från företaget är objektiv, dock kan information från andra användare vara falska och man bör därför se efter kommentarer med verifierings loggan, då dessa är verifierade svar som företaget håller med om.",
    "about.whatwedo.p2": "Genom vårt community får du snabba, pedagogiska svar på svenska som är anpassade för unga. Vi förklarar komplexa begrepp på ett enkelt sätt med exempel som är relevanta för din vardag.",
    "about.values.title": "Våra värderingar",
    "about.values.anonymous.title": "Total anonymitet",
    "about.values.anonymous.desc": "Ingen registrering krävs. Du kan ställa alla frågor utan att oroa dig för din integritet eller vad andra ska tycka.",
    "about.values.objective.title": "Objektiv information",
    "about.values.objective.desc": "All information kommer från trovärdiga källor. Vi är inte kopplade till några finansiella produkter och ger aldrig specifika rekommendationer.",
    "about.values.young.title": "För unga",
    "about.values.young.desc": "Vi använder enkla förklaringar och konkreta exempel som är relevanta för dig som ung. Inga krångliga finanstermer utan tydlighet.",
    "about.values.community.title": "Community-driven",
    "about.values.community.desc": "Ställ frågor till andra unga i samma situation och lära av varandra genom vårt community.",
    "about.help.title": "Hur vi kan hjälpa dig",
    "about.help.basics.title": "Lär dig grunderna",
    "about.help.basics.desc": "Få förklaringar om allt från buffertar och sparande till aktier och fonder",
    "about.help.plan.title": "Planera din ekonomi",
    "about.help.plan.desc": "Få tips på hur du kan börja spara och planera för framtiden",
    "about.help.avoid.title": "Undvik fallgropar",
    "about.help.avoid.desc": "Lär dig om vanliga misstag och hur du kan undvika dem",
    "about.help.disclaimer.title": "Viktig information om investeringar",
    "about.help.disclaimer.desc": "Investeringar i värdepapper såsom aktier och fonder innebär alltid en risk och kan leda till både vinst och förlust. Historisk avkastning är ingen garanti för framtida utveckling. Det är alltid möjligt att förlora hela det investerade kapitalet. Informationen på denna plattform utgör inte finansiell rådgivning och ska inte ersätta professionell rådgivning från en auktoriserad rådgivare.",
    "about.cta.title": "Redo att börja?",
    "about.cta.subtitle": "Börja din resa mot bättre ekonomisk kunskap idag – helt gratis och anonymt",
    "about.cta.button1": "Chatta med AI",
    "about.cta.button2": "Se vanliga frågor",
    
    // Auth page
    "auth.back": "Tillbaka",
    "auth.title": "NextGen Finance",
    "auth.subtitle": "Logga in för att ställa frågor till communityn",
    "auth.email.info.title": "Varför vi ber om email:",
    "auth.email.info.desc": "Din e-postadress används endast för att skydda företaget från spam och missbruk. Vi använder inte din information på något annat sätt.",
    "auth.signin": "Logga in",
    "auth.signup": "Skapa konto",
    "auth.email": "E-postadress",
    "auth.password": "Lösenord",
    "auth.password.min": "Lösenord (minst 6 tecken)",
    "auth.signin.button": "Loggar in...",
    "auth.signup.button": "Skapar konto...",
    
    // Chat page
    "chat.title": "Finansassistenten",
    "chat.subtitle": "Din AI-guide till privatekonomi",
    "chat.welcome.title": "Välkommen till Finansassistenten!",
    "chat.welcome.subtitle": "Ställ frågor om privatekonomi, sparande och investeringar. Jag ger dig objektiv information baserad på trovärdiga källor.",
    "chat.login.required": "Du måste vara",
    "chat.login.link": "inloggad",
    "chat.login.suffix": "för att chatta med AI:n.",
    
    // Questions page
    "questions.title": "Frågor & Svar",
    "questions.faq.title": "Vanliga frågor",
    "questions.faq.subtitle": "Här hittar du svar på de vanligaste frågorna om privatekonomi",
    "questions.community.title": "Fråga communityn",
    "questions.community.subtitle": "Ställ frågor till andra och få svar från communityn",
    "questions.ask.title": "Ställ din fråga",
    "questions.ask.placeholder": "Vad vill du fråga communityn?",
    "questions.ask.button": "Skicka fråga",
    "questions.ask.submitting": "Skickar...",
    "questions.latest": "Senaste frågorna",
    "questions.answer.placeholder": "Skriv ditt svar här...",
    "questions.answer.button": "Skicka svar",
    "questions.answer.submitting": "Skickar...",
    "questions.answer.count": "svar",
    "questions.answer.first": "Bli först att svara",
    "questions.answer.show": "Visa",
    "questions.verified": "Verifierat av företaget",
    "questions.verify": "Verifiera",
    "questions.unverify": "Ta bort verifiering",
    
    // Profile page
    "profile.back": "Tillbaka",
    "profile.title": "Min Profil",
    "profile.subtitle": "Hantera dina kontoinställningar",
    "profile.email": "E-postadress",
    "profile.password.title": "Byt Lösenord",
    "profile.password.subtitle": "Uppdatera ditt lösenord här",
    "profile.password.new": "Nytt lösenord",
    "profile.password.confirm": "Bekräfta nytt lösenord",
    "profile.password.min": "Minst 6 tecken",
    "profile.password.repeat": "Upprepa lösenordet",
    "profile.password.update": "Uppdatera lösenord",
    "profile.password.updating": "Uppdaterar...",
    "profile.signout": "Logga ut",
    
    // Chat Input
    "chatinput.placeholder": "Skriv din fråga om ekonomi här...",
    "chatinput.hint": "Tryck Enter för att skicka, Shift+Enter för ny rad",
    
    // Misc
    "nav.tabs.faq": "Vanliga frågor",
    "nav.tabs.community": "Community-frågor",
    
    // AI language code
    "ai.language": "sv",
    
    // Chat toasts
    "chat.toast.ratelimit.title": "För många förfrågningar",
    "chat.toast.ratelimit.desc": "Vänta en stund och försök igen.",
    "chat.toast.unavailable.title": "Tjänsten är inte tillgänglig",
    "chat.toast.unavailable.desc": "Kontakta support för hjälp.",
    "chat.toast.error.title": "Ett fel uppstod",
    "chat.toast.error.desc": "Kunde inte skicka meddelandet. Försök igen.",
    
    // Questions page validations and toasts
    "questions.validation.question.min": "Frågan måste vara minst 10 tecken",
    "questions.validation.question.max": "Frågan får vara max 1000 tecken",
    "questions.validation.answer.min": "Svaret måste vara minst 10 tecken",
    "questions.validation.answer.max": "Svaret får vara max 2000 tecken",
    "questions.toast.login.title": "Inloggning krävs",
    "questions.toast.login.desc": "Du måste vara inloggad för att ställa frågor",
    "questions.toast.login.answer": "Du måste vara inloggad för att svara på frågor",
    "questions.toast.error.title": "Något gick fel",
    "questions.toast.error.question": "Kunde inte skicka din fråga. Försök igen.",
    "questions.toast.error.answer": "Kunde inte skicka ditt svar. Försök igen.",
    "questions.toast.error.verify": "Kunde inte uppdatera verifiering",
    "questions.toast.success.question.title": "Fråga skickad!",
    "questions.toast.success.question.desc": "Din fråga har skickats till communityn",
    "questions.toast.success.answer.title": "Svar skickat!",
    "questions.toast.success.answer.desc": "Ditt svar har publicerats",
    "questions.toast.validation.title": "Ogiltig fråga",
    "questions.toast.logout.title": "Utloggad",
    "questions.toast.logout.desc": "Du har loggats ut",
    "questions.toast.verify.title": "Svar verifierat",
    "questions.toast.verify.desc": "Svaret har markerats som verifierat",
    "questions.toast.unverify.title": "Verifiering borttagen",
    "questions.toast.unverify.desc": "Verifieringen har tagits bort från svaret",
    "questions.empty": "Inga frågor än. Var den första att ställa en fråga!",
    "questions.login.suffix": "för att ställa frågor. Dina frågor förblir anonyma för andra användare.",
    "questions.login.answer.suffix": "för att svara på frågor.",
    
    // Profile page
    "profile.password.validation": "Lösenordet måste vara minst 6 tecken",
    "profile.toast.mismatch.title": "Lösenorden matchar inte",
    "profile.toast.mismatch.desc": "Se till att båda lösenorden är identiska",
    "profile.toast.invalid.title": "Ogiltigt lösenord",
    "profile.toast.error.title": "Fel vid lösenordsändring",
    "profile.toast.success.title": "Lösenordet har uppdaterats",
    "profile.toast.success.desc": "Ditt nya lösenord har sparats",
    
    // Auth validation messages
    "auth.validation.email": "Ogiltig e-postadress",
    "auth.validation.password.min": "Lösenordet måste vara minst 8 tecken",
    "auth.validation.password.max": "Lösenordet är för långt",
    "auth.validation.password.uppercase": "Måste innehålla minst en stor bokstav",
    "auth.validation.password.lowercase": "Måste innehålla minst en liten bokstav",
    "auth.validation.password.number": "Måste innehålla minst en siffra",
    "auth.toast.already.title": "E-postadressen är redan registrerad",
    "auth.toast.already.desc": "Försök logga in istället.",
    "auth.toast.success.title": "Konto skapat!",
    "auth.toast.success.desc": "Du kan nu logga in.",
    "auth.toast.signin.error": "Fel e-postadress eller lösenord",
    "auth.toast.signin.success": "Välkommen tillbaka!",
    
    // Chat auth toast
    "chat.toast.auth.desc": "Du måste vara inloggad för att chatta med AI:n.",
  },
  en: {
    // Index/Landing page
    "hero.badge": "100% Free & Anonymous",
    "hero.title.learn": "Learn about",
    "hero.title.finance": "personal finance",
    "hero.description": "Ask anonymous questions about savings, investments, and finance. Get objective answers based on reliable sources – completely free.",
    "hero.button.chat": "Start chatting for free",
    "hero.button.faq": "FAQ",
    "hero.button.more": "Read more",
    "features.title": "Why use us?",
    "features.subtitle": "We help young people take control of their finances through objective information and AI-driven guidance",
    "features.anonymous.title": "100% Anonymous",
    "features.anonymous.desc": "No registration required. Ask questions without worrying about your privacy.",
    "features.objective.title": "Objective Information",
    "features.objective.desc": "All answers are based on reliable sources. No specific product recommendations.",
    "features.young.title": "For Youth",
    "features.young.desc": "Simple explanations with examples that are relevant to you.",
    "howto.title": "How it works",
    "howto.subtitle": "Three simple steps to better financial knowledge",
    "howto.step1.title": "Ask your question",
    "howto.step1.desc": "Click \"Start chatting\" and write your question about finance",
    "howto.step2.title": "Get instant answers",
    "howto.step2.desc": "Our AI assistant gives you objective information based on reliable sources",
    "howto.step3.title": "Learn more",
    "howto.step3.desc": "Ask follow-up questions and deepen your knowledge about personal finance",
    "cta.title": "Ready to take control of your finances?",
    "cta.subtitle": "Start your journey to better financial knowledge today – completely free and anonymous",
    "cta.button": "Start your first chat",
    "footer.disclaimer": "Finance Assistant provides general education about personal finance. We do not provide personal financial advice.",
    
    // Navigation
    "nav.profile": "Profile",
    "nav.login": "Log in",
    "nav.logout": "Log out",
    "nav.home": "Home",
    
    // About page
    "about.title": "About us",
    "about.hero.title": "We help young people take control of their",
    "about.hero.title.economy": "finances",
    "about.hero.subtitle": "Our goal is to make financial education accessible, understandable, and completely free for all young people",
    "about.whatwedo.title": "What we do",
    "about.whatwedo.p1": "We run a platform where young people can ask anonymous questions about personal finance, savings, and investments. All information from the company is objective, however information from other users may be false and you should therefore look for comments with the verification badge, as these are verified answers that the company agrees with.",
    "about.whatwedo.p2": "Through our AI assistant and community, you get quick, educational answers that are adapted for young people. We explain complex concepts in a simple way with examples that are relevant to your everyday life.",
    "about.values.title": "Our values",
    "about.values.anonymous.title": "Total anonymity",
    "about.values.anonymous.desc": "No registration required. You can ask all questions without worrying about your privacy or what others will think.",
    "about.values.objective.title": "Objective information",
    "about.values.objective.desc": "All information comes from reliable sources. We are not connected to any financial products and never give specific recommendations.",
    "about.values.young.title": "For youth",
    "about.values.young.desc": "We use simple explanations and concrete examples that are relevant to you as a young person. No complicated financial terms, just clarity.",
    "about.values.community.title": "Community-driven",
    "about.values.community.desc": "In addition to the AI assistant, you can also ask questions to other young people in the same situation and learn from each other.",
    "about.help.title": "How we can help you",
    "about.help.basics.title": "Learn the basics",
    "about.help.basics.desc": "Get explanations about everything from emergency funds and savings to stocks and funds",
    "about.help.plan.title": "Plan your finances",
    "about.help.plan.desc": "Get tips on how to start saving and planning for the future",
    "about.help.avoid.title": "Avoid pitfalls",
    "about.help.avoid.desc": "Learn about common mistakes and how to avoid them",
    "about.cta.title": "Ready to start?",
    "about.cta.subtitle": "Start your journey to better financial knowledge today – completely free and anonymous",
    "about.cta.button1": "Chat with AI",
    "about.cta.button2": "See FAQ",
    
    // Auth page
    "auth.back": "Back",
    "auth.title": "NextGen Finance",
    "auth.subtitle": "Log in to ask questions to the community",
    "auth.email.info.title": "Why we ask for email:",
    "auth.email.info.desc": "Your email address is only used to protect the company from spam and abuse. We do not use your information in any other way.",
    "auth.signin": "Log in",
    "auth.signup": "Create account",
    "auth.email": "Email address",
    "auth.password": "Password",
    "auth.password.min": "Password (at least 6 characters)",
    "auth.signin.button": "Logging in...",
    "auth.signup.button": "Creating account...",
    
    // Chat page
    "chat.title": "Finance Assistant",
    "chat.subtitle": "Your AI guide to personal finance",
    "chat.welcome.title": "Welcome to Finance Assistant!",
    "chat.welcome.subtitle": "Ask questions about personal finance, savings, and investments. I'll give you objective information based on reliable sources.",
    "chat.login.required": "You must be",
    "chat.login.link": "logged in",
    "chat.login.suffix": "to chat with the AI.",
    
    // Questions page
    "questions.title": "Q&A",
    "questions.faq.title": "FAQ",
    "questions.faq.subtitle": "Here you'll find answers to the most common questions about personal finance",
    "questions.community.title": "Ask the community",
    "questions.community.subtitle": "Ask questions to others and get answers from the community",
    "questions.ask.title": "Ask your question",
    "questions.ask.placeholder": "What would you like to ask the community?",
    "questions.ask.button": "Submit question",
    "questions.ask.submitting": "Submitting...",
    "questions.latest": "Latest questions",
    "questions.answer.placeholder": "Write your answer here...",
    "questions.answer.button": "Submit answer",
    "questions.answer.submitting": "Submitting...",
    "questions.answer.count": "answer",
    "questions.answer.first": "Be the first to answer",
    "questions.answer.show": "Show",
    "questions.verified": "Verified by company",
    "questions.verify": "Verify",
    "questions.unverify": "Remove verification",
    
    // Profile page
    "profile.back": "Back",
    "profile.title": "My Profile",
    "profile.subtitle": "Manage your account settings",
    "profile.email": "Email address",
    "profile.password.title": "Change Password",
    "profile.password.subtitle": "Update your password here",
    "profile.password.new": "New password",
    "profile.password.confirm": "Confirm new password",
    "profile.password.min": "At least 6 characters",
    "profile.password.repeat": "Repeat password",
    "profile.password.update": "Update password",
    "profile.password.updating": "Updating...",
    "profile.signout": "Sign out",
    
    // Chat Input
    "chatinput.placeholder": "Write your question about finance here...",
    "chatinput.hint": "Press Enter to send, Shift+Enter for new line",
    
    // Misc
    "nav.tabs.faq": "FAQ",
    "nav.tabs.community": "Community questions",
    
    // AI language code
    "ai.language": "en",
    
    // Chat toasts
    "chat.toast.ratelimit.title": "Too many requests",
    "chat.toast.ratelimit.desc": "Please wait a moment and try again.",
    "chat.toast.unavailable.title": "Service unavailable",
    "chat.toast.unavailable.desc": "Please contact support for help.",
    "chat.toast.error.title": "An error occurred",
    "chat.toast.error.desc": "Could not send the message. Please try again.",
    
    // Questions page validations and toasts
    "questions.validation.question.min": "Question must be at least 10 characters",
    "questions.validation.question.max": "Question can be max 1000 characters",
    "questions.validation.answer.min": "Answer must be at least 10 characters",
    "questions.validation.answer.max": "Answer can be max 2000 characters",
    "questions.toast.login.title": "Login required",
    "questions.toast.login.desc": "You must be logged in to ask questions",
    "questions.toast.login.answer": "You must be logged in to answer questions",
    "questions.toast.error.title": "Something went wrong",
    "questions.toast.error.question": "Could not send your question. Please try again.",
    "questions.toast.error.answer": "Could not send your answer. Please try again.",
    "questions.toast.error.verify": "Could not update verification",
    "questions.toast.success.question.title": "Question submitted!",
    "questions.toast.success.question.desc": "Your question has been sent to the community",
    "questions.toast.success.answer.title": "Answer submitted!",
    "questions.toast.success.answer.desc": "Your answer has been published",
    "questions.toast.validation.title": "Invalid question",
    "questions.toast.logout.title": "Logged out",
    "questions.toast.logout.desc": "You have been logged out",
    "questions.toast.verify.title": "Answer verified",
    "questions.toast.verify.desc": "The answer has been marked as verified",
    "questions.toast.unverify.title": "Verification removed",
    "questions.toast.unverify.desc": "Verification has been removed from the answer",
    "questions.empty": "No questions yet. Be the first to ask a question!",
    "questions.login.suffix": "to ask questions. Your questions remain anonymous to other users.",
    "questions.login.answer.suffix": "to answer questions.",
    
    // Profile page
    "profile.password.validation": "Password must be at least 6 characters",
    "profile.toast.mismatch.title": "Passwords don't match",
    "profile.toast.mismatch.desc": "Make sure both passwords are identical",
    "profile.toast.invalid.title": "Invalid password",
    "profile.toast.error.title": "Error changing password",
    "profile.toast.success.title": "Password updated",
    "profile.toast.success.desc": "Your new password has been saved",
    
    // Auth validation messages
    "auth.validation.email": "Invalid email address",
    "auth.validation.password.min": "Password must be at least 8 characters",
    "auth.validation.password.max": "Password is too long",
    "auth.validation.password.uppercase": "Must contain at least one uppercase letter",
    "auth.validation.password.lowercase": "Must contain at least one lowercase letter",
    "auth.validation.password.number": "Must contain at least one number",
    "auth.toast.already.title": "Email already registered",
    "auth.toast.already.desc": "Try logging in instead.",
    "auth.toast.success.title": "Account created!",
    "auth.toast.success.desc": "You can now log in.",
    "auth.toast.signin.error": "Invalid email or password",
    "auth.toast.signin.success": "Welcome back!",
    
    // Chat auth toast
    "chat.toast.auth.desc": "You must be logged in to chat with the AI.",
  },
};
